import { Button, Popover, PopoverContent, TextArea } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import type { InterestWorkspaceResponse } from 'types/api-responses';

import { updateWorkspaceDescriptionLoadableAtom } from './atoms';
import styles from './workspace-description-editor.module.scss';

interface WorkspaceDescriptionEditorProps {
  workspace: InterestWorkspaceResponse;
}

const MAX_LENGTH = 2000;

/**
 * Inline pencil control for the workspace's free-text description, rendered
 * next to the Description line in the Summary card.
 *
 * Renders an icon-only pencil button that opens a Carbon `Popover` with a
 * multi-line textarea + Save/Cancel. Saving PATCHes the workspace; the popover
 * closes on success and the Summary card re-renders from the refetched atom.
 * Hidden entirely once the workspace is promoted — a promoted workspace is a
 * frozen snapshot, mirroring the rename pencil.
 *
 * @param {WorkspaceDescriptionEditorProps} props - Component props.
 * @return {JSX.Element} The rendered editor control (empty when promoted).
 */
export const WorkspaceDescriptionEditor = ({
  workspace,
}: WorkspaceDescriptionEditorProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(workspace.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, updateDescription] = useAtom(updateWorkspaceDescriptionLoadableAtom);
  // Guards against a double-submit if Save is clicked while the request is
  // already in flight (mirrors WorkspaceNameEditor).
  const submittingRef = useRef(false);
  const promoted = workspace.promoted;

  // Resync the draft from the record whenever it changes externally (another
  // tab edits it, the atom refetches) — but only while the popover is closed,
  // so we never clobber in-progress typing.
  useEffect(() => {
    if (!open) setValue(workspace.description ?? '');
  }, [workspace.description, open]);

  if (promoted) return <></>;

  const openEditor = (): void => {
    setValue(workspace.description ?? '');
    setError(null);
    setOpen(true);
  };

  const cancel = (): void => {
    setOpen(false);
    setValue(workspace.description ?? '');
    setError(null);
  };

  const submit = async (): Promise<void> => {
    if (submittingRef.current) return;
    const trimmed = value.trim();
    if (trimmed === (workspace.description ?? '')) {
      cancel();
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await updateDescription(workspace.id, trimmed);
      setOpen(false);
    } catch {
      setError("Couldn't save description");
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      onRequestClose={() => !saving && setOpen(false)}
      // The pencil sits at the far right of the Summary card. Anchor the
      // popover bottom-left, but let autoAlign (floating-ui) flip/shift it so
      // it stays inside the viewport and repositions on window resize.
      align="bottom-left"
      autoAlign
      className={styles.popover}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-label={
          workspace.description ? 'Edit description' : 'Add description'
        }
        title={workspace.description ? 'Edit description' : 'Add description'}
        aria-expanded={open}
        onClick={() => (open ? cancel() : openEditor())}
      >
        <Edit size={16} />
      </button>
      <PopoverContent className={styles.content}>
        <TextArea
          id={`workspace-${workspace.id}-description`}
          labelText="Description"
          value={value}
          rows={4}
          maxLength={MAX_LENGTH}
          disabled={saving}
          placeholder="Add a note about this workspace…"
          onChange={(e) => setValue(e.target.value)}
        />
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <Button kind="ghost" size="sm" onClick={cancel} disabled={saving}>
            Cancel
          </Button>
          <Button kind="primary" size="sm" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
